import React from 'react';
import { DSBadge, DSCard, DSCardFooterButton, DSIcon } from '../../design-system/primitives';
import { formatMonth, ordinal } from './helpers';
import { formatHandicap } from '../../utils/pingolfScoring';
import { matchplayProfileUrl } from '../../utils/profileUrls';
import BadgeIcon from '../../components/BadgeIcon';

// One tab, several panels, one question: what has the club recognised in you.
// Contribution and badges come from different systems and stay visually
// distinct, but they answer the same thing, so they stop being two destinations.

function StandingTab({
  badges,
  taskStats,
  placements,
  golfSummary,
  matchplayProfile,
  currentUser,
  onOpenBadge,
  onOpenPlacement,
  onOpenHandicap
}) {
  const standing = taskStats?.monthlyStanding;

  // TWO SCOPES, EACH SAID OUT LOUD.
  //
  // The card used to show lifetime Points/Tasks/Badges under a monthly "Top X%"
  // pill and an "ahead of X% of contributors this month" note, so a member read
  // their lifetime points as this month's. Both numbers are worth showing; what
  // was missing was which was which.
  //
  // A member with no completions this month gets zeros rather than a hidden
  // row: "0 this month" is the answer to the question they asked, and a row
  // that vanishes reads as a bug.
  const monthlyStats = [
    { label: 'Points', value: standing?.points ?? 0 },
    { label: 'Tasks', value: standing?.tasksCompleted ?? 0 }
  ];
  const lifetimeStats = [
    { label: 'Points', value: taskStats?.totalPoints ?? 0 },
    { label: 'Tasks', value: taskStats?.tasksCompleted ?? 0 },
    // Badges are lifetime only - there is no such thing as a monthly badge.
    { label: 'Badges', value: badges.length }
  ];

  const matchplayUrl = currentUser.matchplay_user_id
    ? matchplayProfileUrl(currentUser.matchplay_user_id)
    : null;

  const mpStats = matchplayProfile ? [
    { label: 'Rating', value: matchplayProfile.ratingValue ? Math.round(matchplayProfile.ratingValue).toLocaleString() : null },
    { label: 'Class', value: matchplayProfile.ratingClass },
    {
      label: 'Win rate',
      value: matchplayProfile.gameCount
        ? `${Math.round((matchplayProfile.winCount / matchplayProfile.gameCount) * 100)}%`
        : null
    },
    {
      label: 'Record',
      value: matchplayProfile.gameCount ? `${matchplayProfile.winCount} W, ${matchplayProfile.lossCount} L` : null
    },
    { label: 'Games', value: matchplayProfile.gameCount ?? null },
    { label: 'WPPR rank', value: matchplayProfile.currentWpprRank ? `#${matchplayProfile.currentWpprRank}` : null }
  ].filter((stat) => stat.value !== null && stat.value !== undefined && stat.value !== '') : [];

  return (
    <>
      {badges.length > 0 && (
        <DSCard frame="bars" density="compact" title={`Badge shelf · ${badges.length}`}>
          <div className="mm-badge-grid">
            {badges.map((badge) => (
              <button key={badge.id} type="button" className="mm-badge-tile" onClick={() => onOpenBadge(badge)}>
                <span className="mm-badge-tile__icon" aria-hidden="true"><BadgeIcon badge={badge} size={40} /></span>
                <span className="mm-badge-tile__name">{badge.name}</span>
                <span className="mm-badge-tile__earned">{formatMonth(badge.earned_at)}</span>
              </button>
            ))}
          </div>
        </DSCard>
      )}

      <DSCard frame="bars" density="compact" title="Contribution" actions={standing ? <DSBadge variant="info" size="sm">Top {standing.topPercent}%</DSBadge> : null}>
        <p className="mm-scope-label">This month</p>
        <div className="mm-stat-row">
          {monthlyStats.map((stat) => (
            <div key={`month-${stat.label}`} className="mm-stat">
              <span className="mm-stat__value">{stat.value}</span>
              <span className="mm-stat__label">{stat.label}</span>
            </div>
          ))}
        </div>
        {standing && (
          <p className="mm-note">
            Ahead of {standing.aheadOfPercent}% of contributors this month.
          </p>
        )}

        <p className="mm-scope-label">All time</p>
        <div className="mm-stat-row">
          {lifetimeStats.map((stat) => (
            <div key={`all-${stat.label}`} className="mm-stat">
              <span className="mm-stat__value">{stat.value}</span>
              <span className="mm-stat__label">{stat.label}</span>
            </div>
          ))}
        </div>
      </DSCard>

      {mpStats.length > 0 && (
        <DSCard frame="bars" density="compact" title="MatchPlay" actions={matchplayUrl ? (
            <a className="mm-link" href={matchplayUrl} target="_blank" rel="noopener noreferrer">
              Open on MatchPlay <DSIcon name="arrowUpRight" size="sm" />
            </a>
          ) : null}>
          <div className="mm-kv-grid">
            {mpStats.map((stat) => (
              <div key={stat.label} className="mm-kv">
                <span className="mm-kv__label">{stat.label}</span>
                <span className="mm-kv__value">{stat.value}</span>
              </div>
            ))}
          </div>
          <p className="mm-note">
            Pulled from your MatchPlay profile. Update the ID under Profile to re-sync.
          </p>
        </DSCard>
      )}

      {placements.length > 0 && (
        <DSCard frame="bars" density="compact" title="Competitions" subtitle="Where you finished in each club season." headerTone="strong" flush>
          {placements.map((placement) => {
            const meta = [
              placement.seasonLabel,
              `${placement.playerCount} players`,
              placement.roundsTotal
                ? `${placement.roundsPlayed} of ${placement.roundsTotal} weeks scored`
                : null
            ].filter(Boolean).join(' · ');

            return (
              <button
                key={placement.competitionId}
                type="button"
                className="mm-place-row"
                onClick={() => onOpenPlacement(placement)}
              >
                <span className={`mm-place ${placement.place <= 3 ? 'mm-place--podium' : ''}`}>
                  {ordinal(placement.place)}
                </span>
                <span className="mm-place-row__main">
                  <span className="mm-place-row__name">{placement.name}</span>
                  <span className="mm-place-row__meta">{meta}</span>
                </span>
                {placement.isLive && <DSBadge variant="success" size="sm">Running</DSBadge>}
                <DSIcon name="chevronRight" size="sm" tone="muted" />
              </button>
            );
          })}
        </DSCard>
      )}

      {/* A handicap nobody can check is a handicap players argue about, so the footer bar
          opens the arithmetic, the trend, and every card behind the number above. */}
      {golfSummary && (
        <DSCard
          frame="bars"
          density="compact"
          flush
          title="PinGolf"
          actions={<span className="mm-aside-note">Net scoring, handicap applied</span>}
          footer={(
            <DSCardFooterButton onClick={onOpenHandicap}>
              See how this is calculated
            </DSCardFooterButton>
          )}
        >
          {/* Flush so the stat block and the footer bar each own their own edges; the
              block below brings back the padding the card gave up. */}
          <div className="mm-card-inset">
            <div className="mm-stat-row">
              {[
                // Both are shown as strokes applied to a card, never as the raw average, so they
                // agree with the HCP on the scorecard and with each other.
                { label: 'Playing handicap', value: formatHandicap(golfSummary.playingHandicap) },
                { label: 'Handicap index', value: golfSummary.handicapIndex === null ? 'Seeded' : formatHandicap(golfSummary.handicapIndex, { decimals: 1 }) },
                { label: 'Cards', value: golfSummary.cardsCounted },
                { label: 'Best net', value: golfSummary.bestNet === null ? '-' : golfSummary.bestNet }
              ].map((stat) => (
                <div key={stat.label} className="mm-stat">
                  <span className="mm-stat__value">{stat.value}</span>
                  <span className="mm-stat__label">{stat.label}</span>
                </div>
              ))}
            </div>
            <p className="mm-note">
              {golfSummary.hasHistory
                ? `Your index comes from your last ${golfSummary.cardsCounted} ${golfSummary.cardsCounted === 1 ? 'card' : 'cards'}. Lower net wins.`
                : 'Your handicap is still on its starting seed. Post a card and it starts moving.'}
            </p>
          </div>
        </DSCard>
      )}
    </>
  );
}

export default StandingTab;
